/**
 * logging-service
 * Copied from https://bitbucket.3amlabs.net/projects/WC/repos/logging-service/raw/src/logging-service.js?at=refs%2Fheads%2Fmaster
 * Rewritten to TypeScript
 *
 * More details here: https://confluence.ops.expertcity.com/display/SOA/Logging+Service
 */
import { safeJSONStringify } from './../string-utils.js';
import { flatten, identity } from './logging-service-utils.js';
/**
 * This is the default interval that the LoggingService will wait before sending the logs to the infrastructure.
 * If the config property @property {maxMessages} has been set, then the LoggingService may send the logs earlier if the amount of collected logs is larger.
 */
export const sendInterval_2000ms = 2000;
/**
 * @class LoggingService
 *
 * Per default, will use the fetch API in order to send requests.
 *
 * @param {LoggingConfiguration} config
 *
 */
export class LoggingService {
    constructor(config = {}) {
        this.fields = {};
        this.queue = [];
        this.sendErrors = 0;
        this.url = config.loggingUrl || '';
        this.contentType = config.contentType || 'application/json';
        this.maxMessages = config.maxMessages || 10;
        this.fixed = Object.assign({}, config.fixed);
        this.lastSendAt = Date.now();
        this.agent = config.agent;
    }
    /**
     * @memberOf LoggingService#
     *
     */
    async init() {
        const url = `${this.url}/init?clientTime=${Date.now()}`;
        return new Promise((resolve, reject) => {
            let fetchOptions = undefined;
            if (this.agent) {
                fetchOptions = { agent: this.agent };
            }
            fetch(url, fetchOptions).then(resolve, reject);
        });
    }
    /**
     * @memberOf LoggingService#
     * Send log messages to the configured logging backend.
     *
     * @param {Array|object} messages The messages to log. If `messages`
     *                                is an object, then it is treated as a
     *                                list with a single message.
     *
     * Messages with a 'data' property are flattened. That is, if all
     * of the below conditions are true:
     *   - the value of the data property is an array with a single
     *     plain-object element
     *   - there is no conflict in property names between the message
     *     and data[0]
     * then the properties of data[0] are copied into message and
     * message.data is deleted. It is possible for the 'data' property to be
     * a string. In that case, LoggingService attempts to interpret it as
     * an array or a plain-object. A plain-object is an object which is not
     * part of the DOM.
     *
     * The following example illustrates the message flattening procedure:
     * @example
     *   |  Message                      |  Flatten result                |  Note        |
     *   | {'data': {'non': 'array'}}    | {'data': {'non': 'array'}}     | not an array |
     *   | {'data': [{'a': 'b'}]}        | {'a': 'b'}                     | flattened    |
     *   | {'a': 2, 'data': [{'a': 'b'}]}| {'a': 2, 'data': [{'a': 'b'}]} | conflict     |
     *
     * The resulting request payload which is sent to the logging backend has
     * the following format:
     *
     * { <DYNAMIC-FIELDS>, "entries": [<FLATTENED-MESSAGE-ARRAY>], <FIXED-FIELDS>}
     *
     * where:
     *   -- <DYNAMIC-FIELDS> are the key value pairs configured via the
     *                       LoggingService.field method
     *   -- <FLATTENED-MESSAGE-ARRAY> is the array of messages for which
     *                                flattening has been attempted
     *   -- <FIXED-FIELDS> are the fixed key value pairs provided by config.fixed
     *                     to LoggingService constructor
     */
    async log(messages) {
        if (!this.url) {
            return Promise.reject('No logging URL specified, remote-logging disabled!');
        }
        const entries = (Array.isArray(messages) ? messages : [messages])
            .map(flatten)
            .filter(identity);
        if (entries.length) {
            this.queue.push.apply(this.queue, entries);
            return this.scheduleSend();
        }
        else {
            return Promise.reject('No messages to log');
        }
    }
    /**
     * @memberOf LoggingService#
     *
     * @param {string} key
     * @param {*} [value] Use undefined to unset
     */
    field(key, value) {
        if (typeof value == 'undefined') {
            // eslint-disable-next-line no-prototype-builtins
            if (this.fields.hasOwnProperty(key)) {
                delete this.fields[key];
            }
        }
        else {
            this.fields[key] = value;
        }
    }
    getFixedField(key) {
        return this.fixed[key];
    }
    createFetchParameters(entries) {
        const body = Object.assign(Object.assign(Object.assign({}, this.fields), { entries }), this.fixed);
        const json = safeJSONStringify(body);
        return {
            url: `${this.url}/log`,
            options: {
                method: 'POST',
                headers: {
                    'content-type': this.contentType,
                },
                body: json,
                agent: this.agent,
            },
        };
    }
    async scheduleSend() {
        let delay = sendInterval_2000ms;
        // First stop any timer in progress, we'll recreate one later if needed
        clearTimeout(this.sendTimeout);
        if (this.queue.length < 1 || this.sendPromise) {
            // Nothing to send or outbound request in-flight
            return Promise.resolve();
        }
        else if (this.sendErrors > 0) {
            // Backout slowly if we keep seeing errors
            delay = this.sendErrors * sendInterval_2000ms;
        }
        else if (this.queue.length >= this.maxMessages) {
            // If we have a bunch of messages queued, send as fast as possible
            delay = 0;
        }
        else {
            // if we have been idle for too long, send immediately
            if (Date.now() - this.lastSendAt > sendInterval_2000ms) {
                delay = 0;
            }
        }
        if (delay) {
            return new Promise((resolve, reject) => {
                this.sendTimeout = setTimeout(() => {
                    this.sendLog().then(resolve, reject);
                }, delay);
            });
        }
        else {
            this.sendTimeout = undefined;
            return this.sendLog();
        }
    }
    async sendLog() {
        if (this.sendPromise) {
            return Promise.reject('transferring logs is currently in process');
        }
        const entries = this.queue.splice(0, this.maxMessages);
        if (entries.length < 1) {
            return Promise.reject('not enough entries');
        }
        this.lastSendAt = Date.now();
        const params = this.createFetchParameters(entries);
        const promise = new Promise((resolve, reject) => {
            fetch(params.url, params.options).then(resolve, reject);
        });
        const scheduleNext = () => {
            let another = false;
            if (promise == this.sendPromise) {
                this.sendPromise = undefined;
                another = true;
                this.scheduleSend();
            }
            return another;
        };
        this.sendPromise = promise;
        return promise.then(() => {
            if (scheduleNext()) {
                this.sendErrors = 0;
            }
        }, (error) => {
            this.queue.unshift.apply(this.queue, entries);
            if (!this.sendErrors) {
                console.error(error);
            }
            this.sendErrors++;
            scheduleNext();
        });
    }
}
//# sourceMappingURL=logging-service.js.map